import Icon from "@react-native-vector-icons/material-design-icons";
import DataIcon from "assets/images/defaultIcons/data.svg";
import theme from "assets/theme";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  View,
} from "react-native";
import AlertService from "services/Alert.service";
import { getRelatedAlerts } from "shared/helpers/shared.helpers";
import {
  ALERT_ACCESSOR_SYSTEM,
  DEPENDENCY_TYPE_LOCATION,
  DEPENDENCY_TYPE_ORGANIZATION,
  DEPENDENCY_TYPE_TEAM,
} from "web/constants";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import TooltipText from "webComponentsFormElements/TooltipText.component";
import CustomTextEditor from "webComponentsFormElements/inputs/CustomTextEditor.component";
import FileUpload from "webComponentsFormElements/inputs/FileUpload.component";
import LabelInput from "webComponentsFormElements/inputs/LabelInput.component";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";
import CreateOtherContactModal from "webComponentsScreens/platforms/CreateOtherContactModal.component";
import DependentSelectionModal from "webComponentsScreens/platforms/DependentSelectionModal.component";
import DismissAlertsModal from "webComponentsScreens/platforms/DismissAlertsModal.component";
import ClickStyledText from "webComponentsShared/ClickStyledText.component";
import RelatedAlerts from "webComponentsShared/RelatedAlerts.component";
import { AlertContext } from "webContext/Alert.context";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const PLATFORM_CONTACT_TOOLTIP = `Select contacts that should be points of contact for support issues related to this platform for your organization.
 These contacts will be visible to users when they view details about the platform.`;

const classes = StyleSheet.create({
  root: {
    alignItems: "flex-start",
    justifyContent: "center",
    gap: 20,
    padding: 20,
    minHeight: "100%",
  },
  formRow: {
    gap: 20,
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-evenly",
    "@media (max-width: 768px)": {
      flexDirection: "column",
      gap: 20,
      alignItems: "stretch",
    },
  },
  label: {
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
    paddingBottom: 8,
  },
  content: {
    fontWeight: theme.font.weight.regular,
    color: theme.color.darkGrey,
    paddingLeft: 24,
    paddingBottom: 8,
  },
  noneText: {
    fontStyle: "italic",
  },
  tooltipContainer: {
    zIndex: 2,
  },
  imageSectionContainer: {
    marginTop: 15,
    flex: 1,
  },
  defaultImagesGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 10,
    justifyContent: "flex-start",
    "@media (max-width: 480px)": {
      justifyContent: "center",
    },
  },
  defaultImageOption: {
    width: 60,
    height: 60,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: theme.color.lightGrey,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.color.white,
  },
  defaultImageOptionSelected: {
    borderColor: theme.color.primary,
    borderWidth: 4,
  },
  defaultImageIcon: {
    width: 40,
    height: 40,
  },
  defaultImagesLabel: {
    fontSize: 14,
  },
  defaultImageFallbackText: {
    fontSize: 10,
    textAlign: "center",
  },
  contactsContainer: {
    marginTop: 20,
    gap: 10,
    flex: 1,
  },
  contactsHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  createContactButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    marginLeft: 10,
  },
  createContactIcon: {
    marginRight: 4,
  },
  createContactButtonText: {
    fontSize: 12,
    fontWeight: theme.font.weight.medium,
  },
  contactsCount: {
    fontSize: 14,
    color: theme.color.darkGrey,
    fontStyle: "italic",
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 8,
    paddingHorizontal: 4,
    backgroundColor: theme.color.background,
    borderRadius: 4,
  },
  contactName: {
    fontSize: 16,
    color: theme.color.backgroundText,
  },
  contactJobTitle: {
    fontSize: 14,
    color: theme.color.darkGrey,
    fontStyle: "italic",
  },
  contactInfo: {
    flex: 1,
  },
  contactview: {
    maxHeight: 200,
  },
});

export default function PlatformEditModalForm({
  platform,
  setPlatform,
  dependents,
  setPlatformDependents,
  image,
  setImage,
  setImageBlob,
  selectedOtherContactIds,
  setSelectedOtherContactIds,
  setCanSave,
  canSave,
  isCreating,
}) {
  const {
    teamList,
    locationList,
    defaultPlatformFiles,
    otherContactList,
    organizationColor,
  } = useContext(OrganizationContext);
  const { sortedAllAlerts, getAlerts } = useContext(AlertContext);

  const { showToast } = useContext(ToastContext);
  const [isDependentSelectionShown, setIsDependentSelectionShown] =
    useState(false);
  const [isDismissModalOpen, setIsDismissModalOpen] = useState(false);
  const [isOrganizationDependent, setIsOrganizationDependent] = useState(false);
  const [dependentTeamIds, setDependentTeamIds] = useState(new Set());
  const [dependentLocationIds, setDependentLocationIds] = useState(new Set());
  const [selectedDefaultImage, setSelectedDefaultImage] = useState(null);
  const [otherContactSearchText, setOtherContactSearchText] = useState("");
  const [selectedOtherContacts, setSelectedOtherContacts] = useState(new Set());
  const [isCreateContactModalOpen, setIsCreateContactModalOpen] =
    useState(false);

  const filteredOtherContacts = useMemo(() => {
    if (!otherContactList) {
      return [];
    }

    return otherContactList
      .filter((contact) => {
        const fullName = `${contact.firstName} ${contact.lastName}`;
        const organization = contact.organization || "";
        const email = contact.email || "";

        const searchTerm = otherContactSearchText.toLowerCase();
        return (
          fullName.toLowerCase().includes(searchTerm) ||
          organization.toLowerCase().includes(searchTerm) ||
          email.toLowerCase().includes(searchTerm)
        );
      })
      .sort((a, b) => {
        const aSelected = selectedOtherContacts.has(a.otherContactId);
        const bSelected = selectedOtherContacts.has(b.otherContactId);

        if (aSelected && !bSelected) {
          return -1;
        }
        if (!aSelected && bSelected) {
          return 1;
        }

        const aName = `${a.firstName} ${a.lastName}`.toLowerCase();
        const bName = `${b.firstName} ${b.lastName}`.toLowerCase();
        return aName.localeCompare(bName);
      });
  }, [otherContactList, otherContactSearchText, selectedOtherContacts]);

  const toggleOtherContact = useCallback(
    (otherContactId) => {
      setSelectedOtherContacts((prev) => {
        const newSelected = new Set(prev);
        if (newSelected.has(otherContactId)) {
          newSelected.delete(otherContactId);
        } else {
          newSelected.add(otherContactId);
        }
        return newSelected;
      });

      setSelectedOtherContactIds((prev) => {
        const newIds = new Set(prev);
        if (newIds.has(otherContactId)) {
          newIds.delete(otherContactId);
        } else {
          newIds.add(otherContactId);
        }
        return Array.from(newIds);
      });
      !canSave && setCanSave(true);
    },
    [setSelectedOtherContactIds, canSave, setCanSave],
  );

  const stateClasses = StyleSheet.create({
    labelOnlineOfflineText: {
      fontWeight: theme.font.weight.medium,
      color: platform.isOnline ? theme.color.green : theme.color.red,
      paddingTop: 8,
    },
  });

  const defaultPlatformImages = useMemo(() => {
    const nameMapping = {
      "gsuite-defaultImage.png": { name: "G Suite", id: "gsuite" },
      "AWS-defaultImage.png": { name: "AWS", id: "aws" },
      "paloAltoVPN-defaultImage.png": {
        name: "Palo Alto VPN",
        id: "paloAltoVPN",
      },
      "adobe-defaultImage.png": { name: "Adobe", id: "adobe" },
      "slack-defaultImage.png": { name: "Slack", id: "slack" },
      "office-defaultImage.png": { name: "Office 365", id: "office" },
      "intranet-defaultImage.png": { name: "Intranet", id: "intranet" },
      "azure-defaultImage.png": { name: "Azure", id: "azure" },
      "orchestration-defaultImage.png": {
        name: "Orchestration",
        id: "orchestration",
      },
      "box-defaultImage.png": { name: "Box", id: "box" },
    };

    return defaultPlatformFiles
      .filter(
        (file) => file.organizationId === null && nameMapping[file.fileRef],
      )
      .map((file) => {
        const mapping = nameMapping[file.fileRef];
        return {
          fileId: file.fileId,
          fileName: file.fileName,
          signedUrl: file.signedUrl,
          name: mapping.name,
          id: mapping.id,
        };
      });
  }, [defaultPlatformFiles]);

  useEffect(() => {
    if (!sortedAllAlerts || sortedAllAlerts.size === 0) {
      getAlerts();
    }
  }, [getAlerts, sortedAllAlerts]);

  const relatedAlerts = useMemo(
    () =>
      getRelatedAlerts(
        ALERT_ACCESSOR_SYSTEM,
        platform?.organizationSystemId,
        sortedAllAlerts,
      ),
    [sortedAllAlerts, platform?.organizationSystemId],
  );

  const onStatusChange = (value) => {
    setPlatform((old) => ({ ...old, isOnline: value }));
    if (relatedAlerts.length > 0) {
      setIsDismissModalOpen(true);
    }
    !canSave && setCanSave(true);
  };

  const handleDismissAlerts = async (alerts) => {
    if (alerts.length === 0) {
      setIsDismissModalOpen(false);
      return;
    }
    try {
      const alertIds = alerts.map((alert) => alert.alertId);
      await AlertService.dismissAlerts({ alertIds });
      await getAlerts();
      showToast(
        `${alerts.length} related alert${alerts.length > 1 ? "s" : ""} dismissed`,
        "success",
      );
    } catch (err) {
      showToast("Failed to dismiss alerts.", "error");
      if (__DEV__) {
        console.error("Error dismissing alerts:", err);
      }
    } finally {
      setIsDismissModalOpen(false);
    }
  };

  useEffect(() => {
    setIsOrganizationDependent(
      dependents.some(
        (dependency) =>
          dependency?.dependencyTypeId === DEPENDENCY_TYPE_ORGANIZATION,
      ),
    );

    setDependentTeamIds(
      new Set(
        dependents
          .filter(
            (dependency) =>
              dependency?.dependencyTypeId === DEPENDENCY_TYPE_TEAM,
          )
          .map((dependency) => dependency.dependencyId),
      ),
    );

    setDependentLocationIds(
      new Set(
        dependents
          .filter(
            (dependency) =>
              dependency?.dependencyTypeId === DEPENDENCY_TYPE_LOCATION,
          )
          .map((dependency) => dependency.dependencyId),
      ),
    );
  }, [dependents]);

  const handleDefaultImageSelect = (imageData) => {
    if (
      imageData.fileId === selectedDefaultImage?.fileId ||
      imageData.signedUrl === selectedDefaultImage?.signedUrl
    ) {
      return;
    }
    setSelectedDefaultImage({
      fileId: imageData?.fileId,
      signedUrl: imageData?.signedUrl,
    });
    setImage({
      fileId: imageData?.fileId,
      signedUrl: imageData?.signedUrl,
    });
    setImageBlob(null);
    !canSave && setCanSave(true);
  };

  const dependentTeams = useMemo(
    () => teamList.filter((team) => dependentTeamIds.has(team.teamId)),
    [dependentTeamIds, teamList],
  );

  const dependentLocations = useMemo(
    () =>
      locationList.filter((location) =>
        dependentLocationIds.has(location.locationId),
      ),
    [dependentLocationIds, locationList],
  );

  useEffect(() => {
    if (selectedOtherContactIds && selectedOtherContactIds.length > 0) {
      setSelectedOtherContacts(new Set(selectedOtherContactIds));
    }
  }, [selectedOtherContactIds]);

  useEffect(() => {
    if (isOrganizationDependent) {
      setPlatformDependents([
        {
          dependencyTypeId: DEPENDENCY_TYPE_ORGANIZATION,
          dependencyId: platform.organizationId,
          organizationSystemId: platform.organizationSystemId,
        },
      ]);
    } else {
      const teamDependencies = [...dependentTeamIds].map((teamId) => ({
        dependencyTypeId: DEPENDENCY_TYPE_TEAM,
        dependencyId: teamId,
        organizationSystemId: platform.organizationSystemId,
      }));

      const locationDependencies = [...dependentLocationIds].map(
        (locationId) => ({
          dependencyTypeId: DEPENDENCY_TYPE_LOCATION,
          dependencyId: locationId,
          organizationSystemId: platform.organizationSystemId,
        }),
      );

      setPlatformDependents([...teamDependencies, ...locationDependencies]);
    }
  }, [
    isOrganizationDependent,
    dependentTeamIds,
    dependentLocationIds,
    setPlatformDependents,
    platform.organizationId,
    platform.organizationSystemId,
  ]);

  return (
    <View style={classes.root}>
      <DismissAlertsModal
        open={isDismissModalOpen}
        alerts={relatedAlerts}
        onDismiss={handleDismissAlerts}
        onKeep={() => setIsDismissModalOpen(false)}
      />

      <DependentSelectionModal
        open={isDependentSelectionShown}
        isOrganizationDependent={isOrganizationDependent}
        setIsOrganizationDependent={setIsOrganizationDependent}
        dependentTeamIds={dependentTeamIds}
        setDependentTeamIds={setDependentTeamIds}
        dependentLocationIds={dependentLocationIds}
        setDependentLocationIds={setDependentLocationIds}
        onClose={() => setIsDependentSelectionShown(false)}
        canSave={canSave}
        setCanSave={setCanSave}
      />

      <View style={classes.formRow}>
        <View style={{ flex: 2 }}>
          <LabelInput
            label="Platform Name"
            value={platform.title}
            onChange={(event) => {
              setPlatform((oldPlatform) => ({
                ...oldPlatform,
                title: event.target.value,
              }));
              !canSave && setCanSave(true);
            }}
            required
          />
        </View>
        <View style={{ flex: 1, alignItems: "center" }}>
          <Text style={classes.label}>Platform Status</Text>
          <Switch
            trackColor={{
              false: theme.color.grey,
              true: theme.color.grey,
            }}
            thumbColor={platform.isOnline ? theme.color.green : theme.color.red}
            value={platform.isOnline}
            onValueChange={onStatusChange}
          />
          <Text style={stateClasses.labelOnlineOfflineText}>
            {platform.isOnline ? "Online" : "Offline"}
          </Text>
        </View>
      </View>
      <View style={classes.formRow}>
        <View style={{ flex: 1 }}>
          <PressableContainer
            onPress={() => setIsDependentSelectionShown(true)}
          >
            <View style={classes.tooltipContainer}>
              <TooltipText tooltip="Click to select which teams or places use this platform.">
                <ClickStyledText>
                  Which teams or places are using this platform
                </ClickStyledText>
              </TooltipText>
            </View>

            {isOrganizationDependent ||
            (dependentTeamIds.size === 0 && dependentLocationIds.size === 0) ? (
              <Text style={[classes.content, classes.noneText]}>
                {isOrganizationDependent
                  ? "Entire organization"
                  : "No teams or places selected"}
              </Text>
            ) : (
              <>
                {dependentTeams.map((team, index) => (
                  <Text key={index} style={classes.content}>
                    Team: {team.name}
                  </Text>
                ))}
                {dependentLocations.map((location, index) => (
                  <Text key={index} style={classes.content}>
                    Place: {location.name}
                  </Text>
                ))}
              </>
            )}
          </PressableContainer>
        </View>
        <View style={{ flex: 1 }}>
          {!isCreating && (
            <RelatedAlerts alerts={relatedAlerts} entityType="Platform" />
          )}
        </View>
      </View>

      <View style={classes.formRow}>
        <View style={classes.imageSectionContainer}>
          <Text style={classes.label}>Platform Image</Text>
          <View style={{ width: "25%", alignSelf: "center" }}>
            <FileUpload
              placeholderIcon={DataIcon}
              file={image}
              setFile={setImage}
              setFileBlob={setImageBlob}
              onChange={() => {
                !canSave && setCanSave(true);
                setSelectedDefaultImage(null);
              }}
              signedUrl={image?.signedUrl || selectedDefaultImage?.signedUrl}
            />
          </View>
          <Text style={[classes.label, classes.defaultImagesLabel]}>
            Or choose from defaults:
          </Text>
          <View style={classes.defaultImagesGrid}>
            {defaultPlatformImages.map((imageData) => {
              const isSelected =
                image?.fileId === imageData.fileId ||
                selectedDefaultImage?.fileId === imageData.fileId;
              return (
                <Pressable
                  key={imageData.fileId}
                  style={[
                    classes.defaultImageOption,
                    isSelected && classes.defaultImageOptionSelected,
                  ]}
                  onPress={() => handleDefaultImageSelect(imageData)}
                >
                  {imageData.signedUrl ? (
                    <Image
                      source={{ uri: imageData.signedUrl }}
                      style={classes.defaultImageIcon}
                      resizeMode="contain"
                    />
                  ) : (
                    <Text style={classes.defaultImageFallbackText}>
                      {imageData.name}
                    </Text>
                  )}
                </Pressable>
              );
            })}
          </View>
        </View>

        <View style={classes.contactsContainer}>
          <View style={classes.contactsHeader}>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <TooltipText tooltip={PLATFORM_CONTACT_TOOLTIP}>
                Platform Contacts
              </TooltipText>
              <Pressable
                style={classes.createContactButton}
                onPress={() => setIsCreateContactModalOpen(true)}
              >
                <Icon
                  name="plus"
                  size={16}
                  color={organizationColor}
                  style={classes.createContactIcon}
                />
                <Text
                  style={[
                    classes.createContactButtonText,
                    { color: organizationColor },
                  ]}
                >
                  Create new contact
                </Text>
              </Pressable>
            </View>
            <Text style={classes.contactsCount}>
              {selectedOtherContacts.size} selected
            </Text>
          </View>

          <SearchBar
            value={otherContactSearchText}
            onChangeText={setOtherContactSearchText}
            placeholder="Search contacts..."
          />

          <ScrollView
            style={classes.contactview}
            showsVerticalScrollIndicator={true}
          >
            {filteredOtherContacts.length > 0 ? (
              filteredOtherContacts.map((contact) => {
                const fullName = contact.name;
                const isSelected = selectedOtherContacts.has(
                  contact.otherContactId,
                );

                return (
                  <PressableContainer
                    key={contact.otherContactId}
                    style={classes.contactItem}
                    onPress={() => toggleOtherContact(contact.otherContactId)}
                  >
                    <View style={classes.contactInfo}>
                      <Text style={classes.contactName}>{fullName}</Text>
                      {contact.organization && (
                        <Text style={classes.contactJobTitle}>
                          {contact.organization}
                        </Text>
                      )}
                      {contact.email && (
                        <Text style={classes.contactJobTitle}>
                          {contact.email}
                        </Text>
                      )}
                    </View>
                    <Icon
                      name={isSelected ? "check" : "checkbox-blank-outline"}
                      size={25}
                      color={organizationColor}
                    />
                  </PressableContainer>
                );
              })
            ) : (
              <Text style={classes.content}>
                {otherContactSearchText
                  ? "No contacts found matching your search."
                  : "No contacts available."}
              </Text>
            )}
          </ScrollView>
        </View>
      </View>
      <View style={classes.formRow}>
        <CustomTextEditor
          title="What you need to know about this platform"
          border
          initialText={platform.information}
          onChangeText={(information) => {
            setPlatform((oldPlatform) => ({ ...oldPlatform, information }));
            !canSave && setCanSave(true);
          }}
        />
      </View>

      <CreateOtherContactModal
        visible={isCreateContactModalOpen}
        onClose={() => setIsCreateContactModalOpen(false)}
        onContactCreated={() => {
          setIsCreateContactModalOpen(false);
        }}
      />
    </View>
  );
}
